/**
 * Bike History Page
 * =================
 * Model history and evolution for the motorcycle series.
 */

import { useParams } from 'react-router-dom'
import { useMotorcycle } from '@/hooks/useMotorcycles'
import BikePageHeader from './BikePageHeader'
import { History, Calendar, Gauge, ChevronRight } from 'lucide-react'

// Model history data for the Yamaha XT350 series
const XT350_MODEL_HISTORY = {
  overview: {
    title: 'Yamaha XT350',
    subtitle: 'A Timeless Dual-Sport Icon',
    description:
      "The Yamaha XT350 was Yamaha's mid-range dual-sport motorcycle that bridged the gap between the nimble XT225 and the more powerful XT600. Produced from 1985 to 2000, it earned recognition for balancing trail agility and highway capability with remarkable simplicity and reliability.",
    productionYears: '1985-2000',
    totalYears: 15,
  },
  specs: {
    engine: '346cc air-cooled DOHC single',
    power: '31.5 HP @ 7,500 RPM',
    torque: '29.5 Nm @ 7,000 RPM',
    weight: '120 kg (dry)',
    topSpeed: '145 km/h (90 mph)',
    transmission: '6-speed manual',
    induction: 'Y.D.I.S. (Yamaha Dual Intake System)',
  },
  generations: [
    {
      id: 1,
      name: 'Generation 1: Pioneer Era',
      years: '1985-1989',
      highlight: 'The original design that started it all',
      description:
        'The inaugural XT350 introduced a revolutionary dual-carburetor setup producing 27-30 hp. Yamaha\'s Y.D.I.S. (Dual Intake System) enabled better fuel economy at lower RPMs while allowing more power up top.',
      features: [
        'Air-cooled DOHC 4-valve single cylinder',
        'Y.D.I.S. dual carburetor system (Teikei Y24PV)',
        'Long-travel suspension (~10" front and rear)',
        'Steel semi-double cradle frame',
        'Kick start only',
        '220mm front disc brake, rear drum',
        'Ground clearance: 275mm (10.8")',
      ],
      image: 'https://i0.wp.com/www.bike-urious.com/wp-content/uploads/Yamaha-XT350-Right-Side.jpg?fit=881%2C611&ssl=1',
    },
    {
      id: 2,
      name: 'Generation 2: Refinement Phase',
      years: '1990-1995',
      highlight: 'Mid-life improvements for reliability and comfort',
      description:
        'Yamaha refined the proven platform with subtle but meaningful improvements. The focus was on addressing owner feedback while maintaining the simplicity that made the original successful.',
      features: [
        'Reinforced frame for improved rigidity',
        'Recalibrated dual-carb system for better throttle response',
        'Improved cold-starting reliability',
        'Expanded oil capacity for better heat management',
        'Upgraded front brake disc with improved caliper',
        'Reshaped seat for improved comfort',
        'Updated styling with sharper tank shrouds',
        'New graphics packages',
      ],
      image: 'https://timeless-miles.com/wp-content/uploads/2025/10/yamaha-xt350-archive-1.jpg',
    },
    {
      id: 3,
      name: 'Generation 3: Final Years',
      years: '1996-2000',
      highlight: 'Mechanically unchanged, a testament to the design',
      description:
        'The final production years saw the XT350 remain mechanically identical to Generation 2, with only cosmetic updates. This was a testament to how well Yamaha had refined the platform - there was simply nothing left to improve.',
      features: [
        'Mechanically unchanged from Gen 2',
        'Revised graphics and color schemes',
        'Improved seat foam for comfort',
        'Continued kick-start simplicity',
        'Production ended as Yamaha transitioned to fuel-injected models',
      ],
      image: 'https://timeless-miles.com/wp-content/uploads/2025/09/Yamaha-XT350-Left-Side-Featured.jpg',
    },
  ],
  legacy: {
    title: 'Legacy & Impact',
    points: [
      'Renowned for exceptional reliability and simplicity',
      'Parts still widely available through aftermarket suppliers',
      'Strong following among vintage dual-sport enthusiasts',
      'Design influenced future Yamaha dual-sport models',
      'Kick-start only design means no battery dependency',
      'Y.D.I.S. system was innovative for its time',
    ],
  },
  sources: [
    { name: 'Timeless Miles XT350 Archives', url: 'https://timeless-miles.com/the-archive/yamaha-xt350-motorcycle-archives/' },
    { name: 'Motorcycle Specs ZA', url: 'https://www.motorcyclespecs.co.za/model/yamaha/yamaha_xt350%2085.htm' },
    { name: 'CMS Parts Catalog', url: 'https://www.cmsnl.com/en-is/yamaha-xt350-dual-purpose-1985-f-usa_model9111/partslist/' },
  ],
}

// Placeholder for bikes without specific history data
const DEFAULT_HISTORY = {
  overview: {
    title: 'Model History',
    subtitle: 'Coming Soon',
    description: 'Historical information for this motorcycle model has not yet been documented.',
    productionYears: 'Unknown',
    totalYears: 0,
  },
  specs: null,
  generations: [],
  legacy: null,
  sources: [],
}

// Map motorcycle IDs to their history data
function getModelHistory(motorcycleId: string, make: string) {
  // For now, only XT350 has detailed history
  if (motorcycleId === 'MOTO-0001' || make.toLowerCase() === 'yamaha') {
    return XT350_MODEL_HISTORY
  }
  return DEFAULT_HISTORY
}

export default function BikeHistory() {
  const { bikeId } = useParams<{ bikeId: string }>()
  const { data: bike, isLoading, error } = useMotorcycle(bikeId || '')

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load motorcycle</p>
      </div>
    )
  }

  const history = bike ? getModelHistory(bike.motorcycle_id, bike.make) : DEFAULT_HISTORY

  return (
    <div>
      <BikePageHeader bike={bike} isLoading={isLoading} />

      {isLoading ? (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          </div>
        </div>
      ) : history.generations.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
          <div className="text-center py-12">
            <History size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Model History
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Historical information for {bike?.name || 'this motorcycle'} model series has not yet been documented.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-xl p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">{history.overview.title}</h1>
            <p className="text-primary-100 text-lg mb-4">{history.overview.subtitle}</p>
            <p className="text-primary-50 max-w-3xl">{history.overview.description}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <div className="text-primary-200 text-sm">Production</div>
                <div className="text-white font-semibold">{history.overview.productionYears}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <div className="text-primary-200 text-sm">Total Years</div>
                <div className="text-white font-semibold">{history.overview.totalYears} years</div>
              </div>
            </div>
          </div>

          {/* Quick Specs */}
          {history.specs && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Gauge size={20} className="text-primary-600 dark:text-primary-400" />
                Technical Specifications
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Engine</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{history.specs.engine}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Power</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{history.specs.power}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Torque</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{history.specs.torque}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Weight</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{history.specs.weight}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Top Speed</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{history.specs.topSpeed}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Transmission</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{history.specs.transmission}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 md:col-span-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Induction</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{history.specs.induction}</div>
                </div>
              </div>
            </div>
          )}

          {/* Generations Timeline */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Calendar size={20} className="text-primary-600 dark:text-primary-400" />
              Model Evolution
            </h2>

            <div className="space-y-8">
              {history.generations.map((gen, index) => (
                <div key={gen.id} className="relative">
                  {/* Timeline connector */}
                  {index < history.generations.length - 1 && (
                    <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
                  )}

                  <div className="flex gap-6">
                    {/* Timeline dot */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center">
                      <span className="text-primary-700 dark:text-primary-400 font-bold">{gen.id}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-8">
                      <div className="flex flex-col lg:flex-row gap-6">
                        {/* Text content */}
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{gen.name}</h3>
                          <div className="text-primary-600 dark:text-primary-400 font-medium mb-2">{gen.years}</div>
                          <p className="text-gray-600 dark:text-gray-300 italic mb-3">{gen.highlight}</p>
                          <p className="text-gray-600 dark:text-gray-300 mb-4">{gen.description}</p>

                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                              Key Features
                            </h4>
                            <ul className="space-y-1">
                              {gen.features.map((feature, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                  <ChevronRight size={16} className="text-primary-500 flex-shrink-0 mt-0.5" />
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Image */}
                        {gen.image && (
                          <div className="lg:w-80 flex-shrink-0">
                            <img
                              src={gen.image}
                              alt={`${gen.name} - ${gen.years}`}
                              className="w-full h-48 lg:h-56 object-cover rounded-lg shadow-md"
                              onError={(e) => {
                                // Hide image on error
                                (e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Legacy Section */}
          {history.legacy && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <History size={20} className="text-primary-600 dark:text-primary-400" />
                {history.legacy.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-3">
                {history.legacy.points.map((point, i) => (
                  <div key={i} className="flex items-start gap-2 text-gray-600 dark:text-gray-300">
                    <ChevronRight size={16} className="text-primary-500 flex-shrink-0 mt-1" />
                    {point}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sources */}
          {history.sources.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Reference Sources</h3>
              <div className="flex flex-wrap gap-4">
                {history.sources.map((source, i) => (
                  <a
                    key={i}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {source.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
